import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Printer } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { BUSINESS } from "@/data/business";
import { Button } from "@/components/ui/button";

const NAV = [
  { key: "nav_services", href: "#services" },
  { key: "nav_pricing", href: "#pricing" },
  { key: "nav_gallery", href: "#gallery" },
  { key: "nav_area", href: "#area" },
];

export const Header = () => {
  const { lang, toggle, tr } = useLang();
  const { business, waLink } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-white"}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <a href="#top" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground">
              <Printer size={20} />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">Creative Prints</span>
              <span className={`text-[10px] uppercase tracking-widest text-amber-700 ${lang === "kn" ? "font-kn" : ""}`}>
                {lang === "kn" ? BUSINESS.tagline_kn : BUSINESS.tagline_en}
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <a
                key={n.key}
                href={n.href}
                data-testid={`nav-${n.key}`}
                className={`text-sm font-semibold text-slate-600 hover:text-primary transition-colors ${lang === "kn" ? "font-kn" : ""}`}
              >
                {tr(n.key)}
              </a>
            ))}
            <Link to="/design" data-testid="nav-design-studio" className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-bold text-amber-800 hover:bg-amber-200 transition-colors">
              ✨ Design Studio
            </Link>
            <Link to="/shop" data-testid="nav-shop" className="text-sm font-semibold text-slate-600 hover:text-primary transition-colors">
              Order Online
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="language-switcher"
              onClick={toggle}
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-primary hover:text-primary transition-colors"
            >
              {lang === "en" ? "ಕನ್ನಡ" : "EN"}
            </button>
            <a href={`tel:${business.phone}`} data-testid="header-call-btn" className="hidden sm:inline-flex">
              <Button variant="outline" className="gap-2 border-slate-300">
                <Phone size={16} /> <span className="hidden md:inline">{tr("call")}</span>
              </Button>
            </a>
            <a href="#quote" data-testid="header-quote-btn" className="hidden sm:inline-flex">
              <Button className="gap-2 bg-primary hover:bg-amber-700 text-white font-semibold">
                {tr("get_quote")}
              </Button>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden grid h-9 w-9 place-items-center rounded-md border border-slate-300"
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1">
          {NAV.map((n) => (
            <a
              key={n.key}
              href={n.href}
              onClick={() => setOpen(false)}
              className={`block rounded-md px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 ${lang === "kn" ? "font-kn" : ""}`}
            >
              {tr(n.key)}
            </a>
          ))}
          <div className="flex gap-2 pt-2">
            <a href={`tel:${business.phone}`} className="flex-1">
              <Button variant="outline" className="w-full gap-2"><Phone size={16} /> {tr("call")}</Button>
            </a>
            <a href={waLink()} target="_blank" rel="noreferrer" className="flex-1">
              <Button className="w-full gap-2 bg-[#25D366] hover:bg-[#1da851] text-white"><MessageCircle size={16} /> {tr("whatsapp")}</Button>
            </a>
          </div>
          <Link to="/admin" className="block pt-2 text-center text-xs font-semibold text-slate-500 underline">
            {tr("nav_admin")}
          </Link>
          <Link to="/design" data-testid="mobile-design-studio" className="mt-2 block rounded-md bg-amber-100 px-3 py-2.5 text-center text-sm font-bold text-amber-800">
            ✨ Open Design Studio
          </Link>
          <Link to="/shop" data-testid="mobile-shop" className="mt-2 block rounded-md border border-slate-200 px-3 py-2.5 text-center text-sm font-bold text-slate-700">
            Order Online
          </Link>
        </div>
      )}
    </header>
  );
};
